//codex-- Map the existing due-contract API without changing maintenance calculations or permissions.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GoogleMap, InfoWindowF, MarkerF, useLoadScript } from '@react-google-maps/api';
import PageToolbar, { ToolbarField } from './PageToolbar';
import { contractMapAddress, getContractMapPeriod, groupContractMapAddresses, resolveContractMapAddresses } from '../utils/contractMap';
//codex-- Open the existing inspection form from a contract's pin actions.
import MapoutInspectionModal from '../components/MapoutInspectionModal';

//codex-- Stable loader/map options avoid repeated script loads and fit a responsive dashboard card.
const MAP_LIBRARIES = ['places'];
const MAP_STYLE = { width: '100%', height: 'min(68vh, 680px)', minHeight: '420px' };
const MAP_OPTIONS = { streetViewControl: false, mapTypeControl: false, fullscreenControl: true };
const EMPTY_CENTER = { lat: 39.8, lng: -98.6 };
const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1).padStart(2, '0'), label: new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' }) }));

//codex-- Keep contract links and popup/list text rendered through React, including user-entered names and addresses.
//codex-- Each contract sharing a pin gets its own edit and new-inspection action.
function ContractSummary({ contract, onNewInspection }) {
  return <div className="min-w-0 space-y-1 break-words text-sm">
    {onNewInspection ? <p className="font-semibold">{contract.customer_name || 'Unnamed customer'} · Contract #{contract.maintenance_id}</p> : <Link className="font-semibold text-blue-800 underline" to={`/maintenance_contract/edit/${encodeURIComponent(contract.maintenance_id)}`}>
      {contract.customer_name || 'Unnamed customer'} · Contract #{contract.maintenance_id}
    </Link>}
    <p>{contractMapAddress(contract) || 'No job-site address'}</p>
    {contract.contract_details?.job_site_address2 && <p>{contract.contract_details.job_site_address2}</p>}
    <p className="text-slate-600">Due: {contract.next_due_date || 'Not available'}</p>
    {/* //codex-- Use explicit buttons only in the pin popup; unmapped contract summaries keep their existing link. */}
    {onNewInspection && <div className="flex flex-wrap gap-2 pt-2">
      <Link className="inline-flex min-h-[40px] items-center rounded-lg border border-blue-200 bg-blue-100 px-3 py-2 font-semibold text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        to={`/maintenance_contract/edit/${encodeURIComponent(contract.maintenance_id)}`}>View/Edit Contract</Link>
      <button type="button" className="min-h-[40px] rounded-lg border border-green-200 bg-green-100 px-3 py-2 font-semibold text-green-900 focus:outline-none focus:ring-2 focus:ring-green-500"
        onClick={() => onNewInspection(contract)}>New Inspection</button>
    </div>}
  </div>;
}

//codex-- Show every unmapped contract, including missing/approximate addresses and service failures.
function UnmappedContracts({ entries }) {
  if (!entries.length) return null;
  return <section className="mt-5 rounded-2xl border border-orange-200 bg-orange-50 p-5" aria-label="Unmapped contracts">
    <h2 className="font-semibold text-orange-900">Unmapped contracts ({entries.length})</h2>
    <ul className="mt-3 grid gap-3 md:grid-cols-2">
      {entries.map(({ contract, reason }) => <li key={contract.maintenance_id} className="min-w-0 rounded-lg border border-orange-200 bg-white p-3">
        <ContractSummary contract={contract} /><p className="mt-2 text-sm text-orange-900">{reason}</p>
      </li>)}
    </ul>
  </section>;
}

//codex-- Geocoding and pins run only after a configured Google Maps script loads; stale month work is discarded.
//codex-- Pass the selected contract from the pin popup to the page-owned creation modal.
function ContractMap({ contracts, apiKey, cache, onNewInspection }) {
  const { isLoaded, loadError } = useLoadScript({ googleMapsApiKey: apiKey, libraries: MAP_LIBRARIES });
  const grouped = useMemo(() => groupContractMapAddresses(contracts), [contracts]);
  const [result, setResult] = useState(null);
  const [completed, setCompleted] = useState(0);
  const [map, setMap] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const selected = result?.locations.find(location => location.key === selectedKey);

  //codex-- One cancellable batch per data set, with progress and a visible failure state instead of missing contracts.
  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    setResult(null);
    setCompleted(0);
    setSelectedKey(null);
    const geocoder = new window.google.maps.Geocoder();
    const geocode = address => new Promise(resolve => geocoder.geocode({ address, region: 'us' }, (results, status) => resolve({ results, status })));
    resolveContractMapAddresses(grouped.groups, geocode, { cache, isCancelled: () => cancelled, onProgress: setCompleted })
      .then(data => { if (data && !cancelled) setResult(data); })
      .catch(() => { if (!cancelled) setResult({ locations: [], unmapped: grouped.groups.flatMap(group => group.contracts.map(contract => ({ contract, reason: 'Address lookup failed. Try refreshing the map.' }))), serviceError: 'Address lookup failed. Try refreshing the map.' }); });
    return () => { cancelled = true; };
  }, [isLoaded, grouped, cache]);

  //codex-- Fit all resolved locations; a single pin starts at street level and selecting a list entry pans to its pin.
  useEffect(() => {
    if (!map || !result?.locations.length) return;
    if (result.locations.length === 1) {
      map.setCenter(result.locations[0].position);
      map.setZoom(16);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      result.locations.forEach(location => bounds.extend(location.position));
      map.fitBounds(bounds, 48);
    }
  }, [map, result]);

  if (loadError) return <div role="alert" className="rounded-xl border border-orange-200 bg-orange-50 p-5">
    Google Maps could not load. Check the map configuration and try refreshing.
    <UnmappedContracts entries={contracts.map(contract => ({ contract, reason: 'Map service unavailable.' }))} />
  </div>;
  if (!isLoaded) return <p role="status" className="p-5">Loading Google Maps…</p>;
  const unmapped = [...grouped.unmapped, ...(result?.unmapped || [])];
  const mapped = result?.locations.reduce((count, location) => count + location.contracts.length, 0) || 0;
  return <>
    <div className="mb-4 flex flex-wrap gap-3 text-sm" aria-label="Map totals">
      <span className="rounded-lg border border-slate-200 bg-white px-3 py-2">Due contracts: {contracts.length}</span>
      <span className="rounded-lg border border-green-200 bg-green-50 px-3 py-2">Mapped contracts: {mapped}</span>
      <span className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">Unmapped contracts: {unmapped.length}</span>
      {!result && <span role="status" className="px-3 py-2">Locating addresses: {completed} / {grouped.groups.length}</span>}
    </div>
    {result?.serviceError && <p role="alert" className="mb-4 rounded-lg bg-orange-50 p-3 text-orange-900">{result.serviceError}</p>}
    <div className="grid min-w-0 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
      <div className="min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" aria-label="Due contract map">
        <GoogleMap mapContainerStyle={MAP_STYLE} center={EMPTY_CENTER} zoom={4} options={MAP_OPTIONS} onLoad={setMap} onUnmount={() => setMap(null)}>
          {result?.locations.map(location => <MarkerF key={location.key} position={location.position}
            title={`${location.address} — ${location.contracts.length} contract(s)`}
            label={location.contracts.length > 1 ? String(location.contracts.length) : undefined}
            onClick={() => setSelectedKey(location.key)} />)}
          {selected && <InfoWindowF position={selected.position} onCloseClick={() => setSelectedKey(null)}>
            <div className="max-h-64 max-w-xs space-y-4 overflow-y-auto p-1" aria-label="Selected location contracts">
              {/* //codex-- Actions belong to individual contracts, including locations with multiple due contracts. */}
              {selected.contracts.map(contract => <ContractSummary key={contract.maintenance_id} contract={contract} onNewInspection={onNewInspection} />)}
            </div>
          </InfoWindowF>}
        </GoogleMap>
      </div>
      <section className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Mapped locations">
        <h2 className="font-semibold text-slate-800">Mapped locations ({result?.locations.length || 0})</h2>
        <p className="mt-1 text-xs text-slate-600">Select a location or pin to view its contracts.</p>
        <ul className="mt-3 max-h-[520px] space-y-2 overflow-y-auto">
          {result?.locations.map(location => <li key={location.key}>
            <button type="button" aria-pressed={selectedKey === location.key}
              className={`min-h-[44px] w-full break-words rounded-lg border p-3 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${selectedKey === location.key ? 'border-blue-400 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}
              onClick={() => { setSelectedKey(location.key); map?.panTo(location.position); }}>
              <span className="block font-semibold">{location.address}</span>
              <span className="text-slate-600">{location.contracts.length} contract(s)</span>
            </button>
          </li>)}
        </ul>
        {result && !result.locations.length && <p className="mt-4 text-sm text-slate-600">No locations could be mapped. Review the addresses below.</p>}
      </section>
    </div>
    <UnmappedContracts entries={unmapped} />
  </>;
}

//codex-- Refresh all due contracts for the URL month/year, independently of dashboard search and pagination.
export default function MapoutPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { month, year } = getContractMapPeriod(params);
  const periodKey = `${year}-${month}`;
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState({ key: '', status: 'loading', contracts: [] });
  //codex-- Keep the modal outside the map's InfoWindow portal so closing a pin cannot destroy an inspection draft.
  const [inspectionContract, setInspectionContract] = useState(null);
  const cache = useRef(new Map());
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const apiUrl = process.env.REACT_APP_API_URL;
  const nowYear = new Date().getFullYear();
  const years = [...new Set([...Array.from({ length: 11 }, (_, i) => String(nowYear - 5 + i)), year])].sort();

  //codex-- Abort obsolete API requests and retain the existing login/forbidden response behavior.
  useEffect(() => {
    const controller = new AbortController();
    setData({ key: periodKey, status: 'loading', contracts: [] });
    axios.get(`${apiUrl}/contracts_crud.php`, { params: { getDueContracts: 'getDueContracts', month, year }, signal: controller.signal })
      .then(response => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(response.data?.[0]) || response.data[0].some(contract => !contract || contract.maintenance_id == null || !contract.contract_details)) throw new Error('Invalid contract response');
        setData({ key: periodKey, status: 'ready', contracts: response.data[0] });
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        if (error.response?.status === 401) navigate('/login');
        setData({ key: periodKey, status: 'error', contracts: [], error: error.response?.status === 403 ? 'You do not have access to these contracts.' : 'Due contracts could not be loaded. Try refreshing.' });
      });
    return () => controller.abort();
  }, [apiUrl, month, year, periodKey, refresh, navigate]);

  const loading = data.status === 'loading' || data.key !== periodKey;
  //codex-- Leave native map fullscreen before opening a body-portal dialog, which would otherwise be hidden.
  const openInspection = async contract => {
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
    setInspectionContract(contract);
  };
  return <main className="mx-auto max-w-[1450px] px-4 py-8 sm:px-6 lg:px-8">
    <h1 className="text-2xl font-semibold text-slate-800">Mapout</h1>
    <p className="mt-1 text-sm text-slate-600">Due maintenance contracts by job site.</p>
    <PageToolbar title="Mapout tools" actions={<>
      <Link className="bg-slate-100" to="/maintenance">Back to contracts</Link>
      <button type="button" className="bg-blue-100" disabled={loading} onClick={() => setRefresh(value => value + 1)}>Refresh map</button>
    </>}>
      <ToolbarField label="Month"><select value={month} onChange={event => setParams({ month: event.target.value, year })}>
        <option value="all">All months</option>
        {MONTHS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select></ToolbarField>
      <ToolbarField label="Year"><select value={year} disabled={month === 'all'} onChange={event => setParams({ month, year: event.target.value })}>
        {years.map(value => <option key={value} value={value}>{value}</option>)}
      </select></ToolbarField>
      {month === 'all' && <p className="text-sm text-slate-600">All months includes all due dates, matching the Contracts dashboard.</p>}
    </PageToolbar>
    {loading ? <p role="status" className="p-5">Loading due contracts…</p>
      : data.status === 'error' ? <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-5 text-red-900">{data.error}</p>
      : !data.contracts.length ? <p role="status" className="rounded-xl border border-slate-200 bg-white p-5">No contracts are due for this selection.</p>
      : !apiKey ? <>
        <p role="alert" className="rounded-xl border border-orange-200 bg-orange-50 p-5 text-orange-900">The map is not configured. Ask your administrator to enable the Google Maps configuration. Due contracts: {data.contracts.length}.</p>
        <UnmappedContracts entries={data.contracts.map(contract => ({ contract, reason: 'Mapping is unavailable until Google Maps is configured.' }))} />
      </> : <ContractMap key={`${periodKey}-${refresh}`} contracts={data.contracts} apiKey={apiKey} cache={cache.current} onNewInspection={openInspection} />}
    {/* //codex-- A successful create closes the modal and refreshes due contracts through the existing API. */}
    {inspectionContract && <MapoutInspectionModal contract={inspectionContract} onClose={() => setInspectionContract(null)}
      onSaved={() => { setInspectionContract(null); setRefresh(value => value + 1); }} />}
  </main>;
}
